import type { Signal } from "@suse-o11y/signal";
import pick from "lodash/pick";

import type { TimeRange } from "../../../../../app/state/view/timeline/TimeRange";
import type { MonitorCheckStatus } from "../../../../../common/openapi";
import type { BoundaryComponent } from "../../../../../components/_component-boundary";

import type { PinnedMetric, PinnedMetricComponent } from "./metric";

export interface PinnedMonitorStatus extends MonitorCheckStatus {
	timeRange: Signal<TimeRange>;
}

export type PinnedItemData = PinnedMonitorStatus | PinnedMetric;

export function normalizePinnedMetricComponent(component: BoundaryComponent): PinnedMetricComponent;
export function normalizePinnedMetricComponent(
	component: null | undefined | BoundaryComponent,
): undefined | PinnedMetricComponent;

export function normalizePinnedMetricComponent(
	component: null | undefined | BoundaryComponent,
): undefined | PinnedMetricComponent {
	if (component == null) {
		return undefined;
	}

	if ("data" in component) {
		const identifier = component.data.identifiers?.at(0);

		if (identifier == null) {
			return undefined;
		}

		return {
			identifier,
			name: component.data.name,
			type: component.typeName,
		};
	}

	return {
		identifier: component.identifier,
		name: component.name,
		type: component.type,
	};
}

export interface PinnedItemCataPattern<TResult> {
	MonitorStatus: (monitorStatus: PinnedMonitorStatus) => TResult;
	Metric: (metric: PinnedMetric) => TResult;
}

export abstract class PinnedItem<TData> {
	public static cata<TResult>(data: PinnedItemData, pattern: PinnedItemCataPattern<TResult>): TResult {
		if ("health" in data) {
			return pattern.MonitorStatus(data);
		}

		return pattern.Metric({
			...data,
			// should pick only the properties that are needed
			component: pick(data.component, ["identifier", "name", "type"]),
			metricQueries: data.metricQueries,
		});
	}

	public abstract data: TData;
	public abstract isExpanded: Signal<boolean>;
}
